import { useLayoutEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  CreateOrEditTemplate,
  DesktopTemplate,
  MobileTemplate,
  TemplatePreview,
} from "features/templates/components";
import { TEMPLATE_TYPES } from "features/templates/constants";
import useTemplates from "features/templates/datahooks/useTemplates";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import {
  areTemplatesEqual,
  getTemplateFromCreatedTemplate,
} from "features/templates/utils";

import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { ContentHeader, ContentScroll } from "common/layouts/WorkspaceLayout";

import DesktopEditTemplate from "./DesktopEditTemplate";
import DiscardEditsDialog from "./DiscardEditsDialog";
import MobilePreview from "./MobilePreview";
import TemplateActions from "./TemplateActions";

export default function SelectedTemplate() {
  const isDesktop = useTwBreakpoint("lg");
  const { templateId } = useParams();
  const { allTemplates } = useTemplates();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { accounts } = useSelectedWorkspaceContext();

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>();

  const selectedTemplate = allTemplates?.find(({ id }) => id === templateId);

  //layout effect prevents no template placeholder message flashing
  useLayoutEffect(() => {
    if (selectedTemplate && selectedTemplate.type !== "VOICE_TEMPLATE") {
      setPreviewTemplate(getTemplateFromCreatedTemplate(selectedTemplate));
    }
  }, [selectedTemplate]);

  function updatePreviewTemplate(updatedTemplate: TemplateSchemaType) {
    if (updatedTemplate.type !== "VOICE_TEMPLATE") {
      setPreviewTemplate(updatedTemplate);
    }
  }

  if (selectedTemplate) {
    const { type, id, name } = selectedTemplate;

    return (
      <>
        <CreateOrEditTemplate
          initialData={selectedTemplate}
          onSuccess={() => {
            updatePreviewTemplate(selectedTemplate);
          }}
        >
          {(onSubmit, isLoading, form) => {
            const updatedTemplate = form.watch();
            const isTemplateUnchanged =
              areTemplatesEqual(selectedTemplate, updatedTemplate) &&
              selectedTemplate.name === updatedTemplate.name;

            const previewComponent = updatedTemplate.type !==
              "VOICE_TEMPLATE" && (
              <TemplatePreview
                previewTemplate={previewTemplate}
                updatedTemplate={updatedTemplate}
                onUpdatePreview={() => updatePreviewTemplate(updatedTemplate)}
                senderProfiles={accounts}
                hasError={"message" in form.formState.errors}
              />
            );

            return isDesktop ? (
              <>
                <ContentHeader>
                  <h2 className="text-md-bold lg:text-lg-bold">{`${TEMPLATE_TYPES[type].shortLabel} template`}</h2>

                  <RestrictedComponent
                    visibleForPermissions={["templates_full"]}
                  >
                    <TemplateActions templateId={id} templateName={name} />
                  </RestrictedComponent>
                </ContentHeader>

                <ContentScroll className="p-4 gap-y-4">
                  <DesktopEditTemplate template={selectedTemplate} />
                  <RestrictedComponent
                    enabledForPermissions={["templates_full"]}
                  >
                    {(isDisabled) => (
                      <DesktopTemplate
                        previewComponent={previewComponent}
                        form={form}
                        isDisabled={isDisabled}
                      />
                    )}
                  </RestrictedComponent>
                </ContentScroll>

                {/* Footer */}
                <div className="p-2.5 border-t border-black-200 flex justify-between">
                  <Button
                    variant="secondary-black"
                    onClick={
                      isTemplateUnchanged
                        ? () => navigate({ pathname: "..", search })
                        : () => setIsDiscardOpen(true)
                    }
                    disabled={isLoading}
                  >
                    Close
                  </Button>
                  <RestrictedComponent
                    visibleForPermissions={["templates_full"]}
                  >
                    <Button
                      onClick={onSubmit}
                      isLoading={isLoading}
                      disabled={isTemplateUnchanged}
                    >
                      Save
                    </Button>
                  </RestrictedComponent>
                </div>
              </>
            ) : (
              <MobilePreview template={selectedTemplate}>
                {(goBack) => {
                  return (
                    <MobileTemplate
                      onClose={
                        isTemplateUnchanged
                          ? goBack
                          : () => setIsDiscardOpen(true)
                      }
                      saveButton={
                        <Button
                          onClick={() =>
                            isTemplateUnchanged
                              ? goBack()
                              : onSubmit().then(goBack)
                          }
                          isLoading={isLoading}
                          size="lg"
                        >
                          Save
                        </Button>
                      }
                      previewComponent={previewComponent}
                      updatePreview={() =>
                        updatePreviewTemplate(updatedTemplate)
                      }
                      form={form}
                    />
                  );
                }}
              </MobilePreview>
            );
          }}
        </CreateOrEditTemplate>

        <DiscardEditsDialog
          isOpen={isDiscardOpen}
          setIsOpen={setIsDiscardOpen}
        />
      </>
    );
  }
}
